/**
 * «Rodar coleta completa agora» — ela, 07/10/2026: "desenhe a correção definitiva". A coleta das 02:30 de 07/10 não
 * publicou e ninguém soube; a tela continuou com a M.C. (CMV, frete, comissão) do dia anterior. O botão roda o MESMO
 * pipeline_completo.sh do cron (~1h50) em segundo plano, com retentativa por passo; ele segura a trava do pipeline
 * (o robô das 06:15/12:15/18:15 espera a coleta acabar). Só administrador; o andamento é consultado a cada 30 s.
 */
import { useEffect, useRef, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { estadoColeta, fetchFila, iniciarColeta, type EstadoColetaJob } from './promocoesService'
import { textoColeta } from './logic'
import { usePromo } from './ctx'
import { useBiAuth } from '../../state/BiAuthContext'
import { LARANJA, P, Panel } from './ui'

const decorrido = (desde: string | null | undefined, agora: number) => {
  const t = desde ? new Date(desde).getTime() : NaN
  if (Number.isNaN(t)) return ''
  const min = Math.max(0, Math.round((agora - t) / 60000))
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}`
}

export function RodarColeta() {
  const ctx = usePromo()
  const { user } = useBiAuth()
  const admin = user?.tipo === 'admin'
  const [st, setSt] = useState<EstadoColetaJob | null>(null)
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)
  const estavaRodando = useRef(false)

  const consultar = async () => {
    try {
      const s = await estadoColeta()
      setSt(s)
      // acabou agora: o pipeline republicou a fila e o painel — recarrega
      if (estavaRodando.current && !s.rodando) {
        setMsg(s.status?.ok ? 'coleta terminou — painel recarregado' : 'coleta terminou com falha — veja a linha da coleta acima')
        const f = await fetchFila()
        if (f) ctx.setFila(f)
        await ctx.recarregarBase()
      }
      estavaRodando.current = s.rodando
    } catch { /* serviço fora: o botão já fica desabilitado */ }
  }

  useEffect(() => {
    if (!ctx.escreve) return
    void consultar()
    const timer = window.setInterval(() => { if (estavaRodando.current) void consultar() }, 30000)
    return () => window.clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.escreve])

  const rodar = async () => {
    if (!window.confirm('Rodar a coleta completa agora? Leva ~1h50 (as duas contas: promoções, comissão e frete reais, CMV do Mongo). Enquanto roda, o robô das 06:15/12:15/18:15 espera ela acabar. Confirma?')) return
    setBusy(true); setMsg('iniciando…')
    try {
      const r = await iniciarColeta()
      setSt(r)
      estavaRodando.current = r.rodando || Boolean(r.iniciado)
      setMsg(r.iniciado ? 'coleta iniciada — o andamento atualiza a cada 30 s'
        : r.ja_rodando ? `já há uma coleta ou rodada do robô em andamento${r.erro ? ` (${r.erro})` : ''}`
          : `não iniciou: ${r.erro ?? 'erro desconhecido'}`)
    } catch (e) { setMsg(`erro: ${e instanceof Error ? e.message : String(e)}`) }
    setBusy(false)
  }

  const rodando = Boolean(st?.rodando)
  const cs = st?.status ?? ctx.fila?.coleta_status ?? null
  const linha = textoColeta(cs, ctx.conta.fetched_at, new Date())
  const desde = cs?.ok == null ? cs?.inicio : st?.iniciado_em
  return (
    <Panel title="📥 Rodar coleta completa agora">
      <P>A coleta completa (M.C., comissão e frete reais, CMV do Mongo) roda sozinha às <b>02:30</b>, com <b>2ª chance às 05:00</b> se falhar. Use o botão quando a linha da coleta disser que falhou, ou depois de corrigir um CMV no Mongo. Leva <b>~1h50</b>; o robô das 06:15/12:15/18:15 espera ela acabar.</P>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" color="inherit" disabled={!ctx.escreve || !admin || rodando || busy} onClick={() => void rodar()}
          title={!ctx.escreve ? 'precisa do serviço no ar' : !admin ? 'só administrador' : ''}>{rodando ? 'coleta rodando…' : '▶ Rodar coleta completa agora'}</Button>
        <Typography sx={{ fontSize: 12.5, color: linha.nivel === 'alerta' ? LARANJA : 'text.secondary', fontWeight: linha.nivel === 'alerta' ? 700 : 400 }}>
          {linha.texto}{rodando && desde ? ` · ${decorrido(desde, Date.now())} decorridos` : ''}{rodando && st?.por ? ` · iniciada por ${st.por}` : ''}
        </Typography>
      </Stack>
      {!admin && ctx.escreve ? <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>Só administrador roda a coleta pela tela.</Typography> : null}
      {msg ? <Typography sx={{ fontSize: 12.5, mt: 1 }}>{msg}</Typography> : null}
      {cs?.passos?.length ? (
        <Box sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>
          {cs.passos.map((p, i) => (
            <Box key={i} component="span" sx={{ mr: 1.5, color: p.ok ? 'text.secondary' : LARANJA }} title={p.erro ?? ''}>
              {p.ok ? '✓' : '✗'} {p.nome} ({Math.round(p.segundos / 60)} min{p.tentativas > 1 ? `, tentativa ${p.tentativas}${p.max_tentativas ? `/${p.max_tentativas}` : ''}` : ''})
            </Box>
          ))}
        </Box>
      ) : null}
    </Panel>
  )
}
